import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddOutlined,
  CloudUploadOutlined,
  DeleteOutline,
  ImageNotSupportedOutlined,
} from '@mui/icons-material';
import { api } from '../../helpers/apiUtils';

/**
 * Structured, type-inferred editor for a broker overview card (`card_data`).
 *
 * The overview card renders from ONE merged object per broker — the hardcoded portal
 * `$brokers[slug]` entry overridden by this profile's `card_data`. There is NO universal
 * card schema: the licence field alone has five shapes across the six portals (bg `licenses[]`,
 * ft `regulation_chips[]`, kme `regulation_icon`+`regulation_text`, tt `license`, sw
 * name-hardcoded in the template). So this editor renders the card's ACTUAL keys, inferring the
 * input from each value's type, and offers an "add field" menu of known-but-missing keys (so a
 * card that lacks e.g. `cta_label` can gain it — the exact change Christian needed a dev for).
 *
 * ⚠ SEIT 2026-08-26 werden `affiliate_url` und `risk_notice` NICHT MEHR HIER GEPFLEGT. Sie
 * werden beim Ausspielen aus derselben Quelle abgeleitet wie die Profilseite — Affiliate-Link
 * aus `broker_hub_profiles.affiliate_url`, Risikohinweis aus dem Compliance-Store
 * (`cardPayload.deriveCardPayload`). Vorher waren sie eine zweite, eigenständig gepflegte Kopie
 * derselben Aussage, und sie war bereits auseinandergelaufen: die FT-Karte sagte **81 %**, der
 * Store **80 %** — beide Zahlen gleichzeitig auf derselben Seite.
 *
 * Die beiden Schlüssel werden deshalb hier AUSGEBLENDET (`ABGELEITETE_FELDER`), nicht nur aus dem
 * „Feld hinzufügen"-Menü genommen: dieser Editor rendert die TATSÄCHLICHEN Schlüssel der Karte,
 * also blieben die Altwerte sonst weiter sicht- und bearbeitbar — ein Eingabefeld, dessen
 * Inhalt beim nächsten Ausspielen kommentarlos überschrieben wird. Die Werte bleiben in
 * `card_data` stehen und werden beim Ausspielen ersetzt; sie zu löschen wäre ein Datenverlust
 * ohne Gegenwert.
 */

/**
 * Kartenfelder, die aus der EINEN Quelle abgeleitet werden — nie von Hand.
 * Wer hier etwas herausnimmt, macht daraus wieder eine zweite Quelle.
 */
const ABGELEITETE_FELDER = new Set(['affiliate_url', 'risk_notice']);

/**
 * Seit 2026-10-27 am BROKER gepflegt (Broker bearbeiten → Zahlungsmethoden, eine Auswahl für alle
 * Portale; `node-app/.../zahlungsmittel.ts`). Hier nur noch Anzeige: was die Karte beim Ausspielen
 * zeigt. Ist der Broker noch nicht zentral gepflegt, steht der Altbestand dieser Karte da — auch
 * der nur lesend, damit nicht wieder sechs Kopien auseinanderlaufen.
 */
const AM_BROKER_GEPFLEGT = 'payments';

export type CardData = Record<string, unknown>;

type Kind = 'string' | 'text' | 'number' | 'image' | 'stringList' | 'objectList';

interface KnownField {
  key: string;
  label: string;
  kind: Kind;
  /** Seed row for an empty objectList (so "Zeile hinzufügen" knows the shape). */
  rowTemplate?: Record<string, string>;
  hint?: string;
}

// Superset of the card fields the six portal templates read. Order here = render order;
// keys not listed (unexpected/custom) are appended after, type-inferred.
const KNOWN_FIELDS: KnownField[] = [
  { key: 'name', label: 'Name', kind: 'string' },
  { key: 'logo', label: 'Logo', kind: 'image' },
  {
    key: 'logo_scale',
    label: 'Logo-Größe',
    kind: 'number',
    hint: '1 = Standardgröße dieses Portals. Gilt nur hier, jedes Portal hat eine eigene Box.',
  },
  {
    key: 'slug',
    label: 'Profil-Slug',
    kind: 'string',
    hint: 'URL-Teil zum Brokerprofil, z. B. plus500-erfahrungen',
  },
  { key: 'rating', label: 'Bewertung (0–5)', kind: 'number' },
  {
    key: 'cta_label',
    label: 'Button-Text (CTA)',
    kind: 'string',
    hint: 'z. B. „Zur Kontoeröffnung“. Der Pflicht-Stern * wird automatisch angehängt — hier NICHT mit eintragen.',
  },
  { key: 'facts', label: 'Fakten', kind: 'stringList' },
  {
    key: 'payments',
    label: 'Zahlungsmethoden',
    kind: 'objectList',
    rowTemplate: { src: '', alt: '' },
  },
  {
    key: 'licenses',
    label: 'Lizenzen',
    kind: 'stringList',
    hint: 'Schlüssel: bafin, fca, cysec, fsa (Börse Global)',
  },
  {
    key: 'license',
    label: 'Lizenz',
    kind: 'string',
    hint: 'Schlüssel: bafin, fca, cysec (Trading-Treff)',
  },
  { key: 'regulation_text', label: 'Regulierung – Text', kind: 'string' },
  { key: 'regulation_icon', label: 'Regulierung – Icon', kind: 'image' },
  {
    key: 'regulation_chips',
    label: 'Regulierungs-Chips',
    kind: 'objectList',
    rowTemplate: { type: 'icon', icon: '', alt: '' },
  },
  {
    key: 'prices',
    label: 'Preiszeilen (frei beschriftet)',
    kind: 'objectList',
    rowTemplate: { label: '', value: '' },
    hint:
      'Ersetzt die vier festen Zeilen darunter, wenn gesetzt. Nötig für Broker, deren Zeilen ' +
      'anders heißen — z. B. „Order Aktien & ETFs“ / „Order Krypto“ statt Order Inland/Ausland. ' +
      'Heute nur von Börse Express gelesen.',
  },
  { key: 'depot', label: 'Depotgebühr', kind: 'string' },
  { key: 'order_inland', label: 'Order Inland', kind: 'string' },
  { key: 'order_ausland', label: 'Order Ausland', kind: 'string' },
  { key: 'einlagensicherung', label: 'Einlagensicherung', kind: 'string' },
];

/**
 * Die Logo-Box je Portal — NUR für die Vorschau hier, gerendert wird immer im Portal-CSS.
 * Quelle ist jeweils die `broker-comparison.css`, die die ÜBERSICHTSSEITE lädt (nicht die
 * `broker-profile.css` daneben — die trägt eine zweite, hier wirkungslose `.broker-logo`-Regel):
 *   ft   150x60 fest  (Newspaper-child/assets/css/broker-comparison.css:75)
 *   tt   150x60 fest  (mh-edition-child/assets/css/broker-comparison.css:78)
 *   sw   150x60 fest  (stockworld-v2/assets/css/broker-comparison.css:75)
 *   kme  150x60 fest  (soledad/assets/css/broker-comparison.css, minifiziert)
 *   bex  150x60 fest  (public/css/broker-comparison.css:75)
 *   bg   max-width 200, width 100%, height auto  (boerse-global/…/broker-comparison.css:75)
 * `h: null` = keine Höhendeckelung, die Höhe folgt dem Logo (nur Börse Global).
 */
const LOGO_BOX: Record<string, { w: number; h: number | null }> = {
  ft: { w: 150, h: 60 },
  tt: { w: 150, h: 60 },
  sw: { w: 150, h: 60 },
  kme: { w: 150, h: 60 },
  bex: { w: 150, h: 60 },
  bg: { w: 200, h: null },
};

/** Faktor-Grenzen. Unter 0,4 verschwindet das Logo, über 2 sprengt es die Kartenspalte. */
const LOGO_SCALE_MIN = 0.4;
const LOGO_SCALE_MAX = 2;

const KNOWN_BY_KEY = new Map(KNOWN_FIELDS.map((f) => [f.key, f]));
const KNOWN_ORDER = KNOWN_FIELDS.map((f) => f.key);

const isImageKey = (key: string): boolean =>
  /(^|_)(logo|icon|image|photo)$/i.test(key) || key === 'src';

const inferKind = (key: string, value: unknown): Kind => {
  const known = KNOWN_BY_KEY.get(key);
  if (known) return known.kind;
  if (typeof value === 'number') return 'number';
  if (Array.isArray(value)) {
    const objectRows = value.some((v) => v !== null && typeof v === 'object' && !Array.isArray(v));
    return objectRows ? 'objectList' : 'stringList';
  }
  if (isImageKey(key)) return 'image';
  return 'string';
};

const labelFor = (key: string): string => KNOWN_BY_KEY.get(key)?.label ?? key;

const defaultForKind = (f: KnownField): unknown => {
  switch (f.kind) {
    case 'number':
      return 0;
    case 'stringList':
    case 'objectList':
      return [];
    default:
      return '';
  }
};

/**
 * Coerce known number fields (e.g. `rating`) from the free-text edit string back to a real
 * number at save time, so an edited card keeps `rating: 4.8` (not `"4.8"`). Deterministic —
 * done on save rather than on blur (a synthetic blur doesn't reliably fire React's handler,
 * and blur-coercion breaks mid-typing of decimals). Untouched numbers are already numbers →
 * no-op. Non-numeric input is left as-is rather than destroyed.
 */
export function normalizeCardData(card: CardData): CardData {
  const out: CardData = { ...card };
  for (const f of KNOWN_FIELDS) {
    if (f.kind === 'number' && typeof out[f.key] === 'string') {
      const raw = (out[f.key] as string).trim();
      if (raw !== '' && Number.isFinite(Number(raw))) out[f.key] = Number(raw);
    }
  }
  // Der Faktor geht als Inline-Style in sechs Portal-Templates. Hier begrenzen statt dort:
  // ein vertippter Wert (50 statt 0,5) waere sonst auf jedem Portal eine zerschossene Karte,
  // und die Templates deckeln zwar ebenfalls, aber erst nach dem naechsten Portal-Deploy.
  if (typeof out.logo_scale === 'number' && Number.isFinite(out.logo_scale)) {
    out.logo_scale = Math.min(LOGO_SCALE_MAX, Math.max(LOGO_SCALE_MIN, out.logo_scale));
  }
  return out;
}

/**
 * Resolve an image value to a URL the DASHBOARD browser can actually load for a preview.
 * Stored values are often portal-RELATIVE (`/wp-content/uploads/...`) — correct for the live
 * portal page (same-origin there) but broken in the dashboard (resolves against db.finanztrends.de).
 * Prefix root-relative paths with the portal origin; leave absolute / protocol-relative / data URIs
 * (e.g. rehosted BunnyCDN logos) untouched. Preview-only — never changes the stored/deployed value.
 */
export function resolvePreviewSrc(value: string, baseUrl?: string | null): string {
  if (!value) return value;
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:')) return value;
  if (baseUrl && value.startsWith('/')) return `${baseUrl.replace(/\/$/, '')}${value}`;
  return value;
}

/** Upload an image to BunnyCDN via the shared broker endpoint, return the absolute CDN URL. */
export async function uploadCardImage(file: File): Promise<string> {
  const form = new FormData();
  form.append('image', file);
  // axios sets the multipart boundary for FormData; the `api` instance carries auth + baseURL.
  const res = await api.post('/api/broker-content/upload-image?kind=logos', form);
  const body = res.data as { success: boolean; data?: { url: string }; error?: string };
  if (body.success && body.data?.url) return body.data.url;
  throw new Error(body.error || 'Upload fehlgeschlagen');
}

// ---------- leaf inputs ----------

const ImageInput: React.FC<{
  label: string;
  value: string;
  onChange: (url: string) => void;
  hint?: string;
  onError: (msg: string) => void;
  baseUrl?: string | null;
}> = ({ label, value, onChange, hint, onError, baseUrl }) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  const pick = () => fileRef.current?.click();
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setBusy(true);
    try {
      onChange(await uploadCardImage(file));
    } catch (err) {
      onError((err as Error).message || 'Upload fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box display="flex" gap={1} alignItems="flex-start">
      <Box
        sx={{
          width: 56,
          height: 40,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fafafa',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {value ? (
          <img
            src={resolvePreviewSrc(value, baseUrl)}
            alt={`${label} Vorschau`}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <ImageNotSupportedOutlined fontSize="small" color="disabled" />
        )}
      </Box>
      <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        helperText={hint}
        placeholder="URL oder hochladen →"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Bild hochladen (BunnyCDN)">
                <span>
                  <IconButton size="small" onClick={pick} disabled={busy} edge="end">
                    {busy ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CloudUploadOutlined fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        hidden
        onChange={onFile}
      />
    </Box>
  );
};

const StringListField: React.FC<{
  label: string;
  hint?: string;
  value: string[];
  onChange: (next: string[]) => void;
}> = ({ label, hint, value, onChange }) => (
  <Box>
    <Typography variant="body2" fontWeight={600} gutterBottom>
      {label}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        {hint}
      </Typography>
    )}
    <Box display="flex" flexDirection="column" gap={1}>
      {value.map((item, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: order-stable list, no reordering
        <Box key={i} display="flex" gap={0.5} alignItems="center">
          <TextField
            value={item}
            onChange={(e) => {
              const next = [...value];
              next[i] = e.target.value;
              onChange(next);
            }}
            size="small"
            fullWidth
          />
          <IconButton
            size="small"
            onClick={() => onChange(value.filter((_, j) => j !== i))}
            aria-label="Eintrag entfernen"
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
    <Button
      size="small"
      startIcon={<AddOutlined />}
      onClick={() => onChange([...value, ''])}
      sx={{ mt: 0.5 }}
    >
      Eintrag hinzufügen
    </Button>
  </Box>
);

const ObjectListField: React.FC<{
  fieldKey: string;
  label: string;
  value: Record<string, unknown>[];
  onChange: (next: Record<string, unknown>[]) => void;
  onError: (msg: string) => void;
  baseUrl?: string | null;
}> = ({ fieldKey, label, value, onChange, onError, baseUrl }) => {
  const subKeys = useMemo(() => {
    const set = new Set<string>();
    value.forEach((row) => {
      if (row && typeof row === 'object') Object.keys(row).forEach((k) => set.add(k));
    });
    if (set.size === 0) {
      const tmpl = KNOWN_BY_KEY.get(fieldKey)?.rowTemplate;
      if (tmpl) Object.keys(tmpl).forEach((k) => set.add(k));
    }
    return Array.from(set);
  }, [value, fieldKey]);

  const addRow = () => {
    const tmpl = KNOWN_BY_KEY.get(fieldKey)?.rowTemplate;
    const row: Record<string, unknown> = {};
    (tmpl ? Object.keys(tmpl) : subKeys).forEach((k) => {
      row[k] = tmpl?.[k] ?? '';
    });
    onChange([...value, row]);
  };

  const setCell = (rowIdx: number, sub: string, v: string) => {
    const next = value.map((r, j) => (j === rowIdx ? { ...r, [sub]: v } : r));
    onChange(next);
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} gutterBottom>
        {label}
      </Typography>
      <Box display="flex" flexDirection="column" gap={1.5}>
        {value.map((row, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: order-stable list, no reordering
          <Box key={i} display="flex" gap={0.5} alignItems="flex-start">
            <Box display="flex" flexDirection="column" gap={0.5} flex={1}>
              {subKeys.map((sub) =>
                isImageKey(sub) ? (
                  <ImageInput
                    key={sub}
                    label={sub}
                    value={String(row?.[sub] ?? '')}
                    onChange={(url) => setCell(i, sub, url)}
                    onError={onError}
                    baseUrl={baseUrl}
                  />
                ) : (
                  <TextField
                    key={sub}
                    label={sub}
                    value={String(row?.[sub] ?? '')}
                    onChange={(e) => setCell(i, sub, e.target.value)}
                    size="small"
                    fullWidth
                  />
                ),
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              aria-label="Zeile entfernen"
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Button size="small" startIcon={<AddOutlined />} onClick={addRow} sx={{ mt: 0.5 }}>
        Zeile hinzufügen
      </Button>
    </Box>
  );
};

/**
 * Regler + Vorschau für `logo_scale`.
 *
 * Der Faktor multipliziert die Logo-Box DES PORTALS (siehe LOGO_BOX) — deshalb zeigt die Vorschau
 * die echte Box dieses Portals und nicht eine gedachte Standardgröße: derselbe Wert bedeutet auf
 * FT (feste 150x60-Box) etwas anderes als auf Börse Global (200 px Breite, Höhe frei).
 *
 * Bewusst KEINE Messung der sichtbaren Logo-Höhe im Browser: dafür müssten wir das Bild auf ein
 * Canvas zeichnen, und Portal- wie CDN-Bilder liefern keine CORS-Header — das Canvas wäre
 * verdorben und `getImageData` würfe. Das Auge am Bild ist hier die ehrlichere Anzeige.
 */
const LogoScaleField: React.FC<{
  value: unknown;
  logo: string;
  siteKey: string | null;
  baseUrl?: string | null;
  onChange: (next: number) => void;
}> = ({ value, logo, siteKey, baseUrl, onChange }) => {
  const box = LOGO_BOX[siteKey ?? ''] ?? null;
  const parsed = typeof value === 'number' ? value : Number(String(value ?? '').replace(',', '.'));
  const scale =
    Number.isFinite(parsed) && parsed > 0
      ? Math.min(LOGO_SCALE_MAX, Math.max(LOGO_SCALE_MIN, parsed))
      : 1;
  const src = logo ? resolvePreviewSrc(logo, baseUrl) : '';
  const w = box ? Math.round(box.w * scale) : 0;
  const h = box?.h ? Math.round(box.h * scale) : null;

  return (
    <Box>
      <Typography variant="body2" fontWeight={600}>
        Logo-Größe
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        1 = Standardgröße dieses Portals. Gilt nur hier — jedes Portal hat eine eigene Box.
        {box
          ? ` Aktuell: ${w} px breit${h ? `, ${h} px hoch` : ' (Höhe folgt dem Logo)'}.`
          : ' Vorschau erst im Portal-Profil, das Stammprofil hat keine eigene Box.'}
      </Typography>
      <Box display="flex" gap={2} alignItems="center">
        <Slider
          value={scale}
          min={LOGO_SCALE_MIN}
          max={LOGO_SCALE_MAX}
          step={0.05}
          marks={[{ value: 1, label: '1' }]}
          valueLabelDisplay="auto"
          onChange={(_e, v) => onChange(Array.isArray(v) ? v[0] : v)}
          sx={{ maxWidth: 220 }}
        />
        <TextField
          value={String(value ?? '')}
          onChange={(e) => {
            const n = Number(e.target.value.replace(',', '.'));
            if (Number.isFinite(n)) onChange(n);
          }}
          size="small"
          inputProps={{ inputMode: 'decimal' }}
          sx={{ width: 90 }}
        />
        <Button size="small" onClick={() => onChange(1)} disabled={scale === 1}>
          Zurücksetzen
        </Button>
      </Box>
      {box && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            bgcolor: '#fff',
            overflowX: 'auto',
          }}
        >
          {src ? (
            <Box
              sx={{
                width: w,
                height: h ?? 'auto',
                outline: '1px dashed #c7cdd8',
                outlineOffset: 2,
              }}
            >
              <img
                src={src}
                alt="Logo-Vorschau"
                style={
                  h
                    ? { width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'left' }
                    : { maxWidth: '100%', height: 'auto', display: 'block' }
                }
              />
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Kein Logo gesetzt — erst oben ein Logo hinterlegen.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// ---------- container ----------

interface Props {
  // Functional updater (not a plain next-value) so an async image upload that resolves after
  // the user edited another field / switched profile merges the URL into the LATEST card_data,
  // not the render-time snapshot it closed over. See critical-review 2026-07-22.
  value: unknown;
  onChange: (updater: (prev: CardData) => CardData) => void;
  siteKey: string | null;
  // Portal origin for resolving relative image previews (see resolvePreviewSrc). Preview-only.
  siteBaseUrl?: string | null;
  /** `FullProfile.broker_card_payments` — `null`/fehlend = am Broker nicht zentral gepflegt. */
  zentraleZahlungsmittel?: Array<{ src: string; alt: string }> | null;
}

const CardEditor: React.FC<Props> = ({
  value,
  onChange,
  siteKey,
  siteBaseUrl,
  zentraleZahlungsmittel,
}) => {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);

  const card: CardData = useMemo(
    () => (value && typeof value === 'object' && !Array.isArray(value) ? (value as CardData) : {}),
    [value],
  );

  const orderedKeys = useMemo(() => {
    const keys = Object.keys(card).filter((k) => !ABGELEITETE_FELDER.has(k));
    // Die zentrale Auswahl erscheint auch auf einer Karte, die das Feld noch nicht trägt — der
    // Deploy setzt es dort ebenfalls.
    if (zentraleZahlungsmittel && !keys.includes(AM_BROKER_GEPFLEGT)) keys.push(AM_BROKER_GEPFLEGT);
    return keys.sort((a, b) => {
      const ia = KNOWN_ORDER.indexOf(a);
      const ib = KNOWN_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }, [card, zentraleZahlungsmittel]);

  const missingFields = KNOWN_FIELDS.filter(
    (f) => !(f.key in card) && f.key !== AM_BROKER_GEPFLEGT,
  );

  // All mutations go through functional updaters so a late async upload rebases onto the current
  // card_data instead of the snapshot captured when the upload started.
  const setField = (key: string, v: unknown) => onChange((prev) => ({ ...prev, [key]: v }));
  const removeField = (key: string) =>
    onChange((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  const addField = (f: KnownField) => {
    setAddAnchor(null);
    onChange((prev) => ({ ...prev, [f.key]: defaultForKind(f) }));
  };

  const renderField = (key: string) => {
    if (key === AM_BROKER_GEPFLEGT) {
      const zentral = Array.isArray(zentraleZahlungsmittel);
      const zeilen = zentral
        ? (zentraleZahlungsmittel as Array<{ src: string; alt: string }>)
        : Array.isArray(card[key])
          ? (card[key] as Array<{ src?: unknown; alt?: unknown }>).map((r) => ({
              src: String(r?.src ?? ''),
              alt: String(r?.alt ?? ''),
            }))
          : [];
      return (
        <Box data-testid="karte-zahlungsmittel">
          <Typography variant="subtitle2">Zahlungsmethoden</Typography>
          <Box display="flex" flexWrap="wrap" gap={0.75} my={0.75}>
            {zeilen.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                keine
              </Typography>
            )}
            {zeilen.map((z, i) => (
              <img
                key={`${z.src}-${i}`}
                src={resolvePreviewSrc(z.src, siteBaseUrl)}
                alt={z.alt}
                title={z.alt}
                style={{ height: 18, width: 'auto' }}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" component="div">
            {zentral
              ? 'Kommt aus der Auswahl am Broker (Übersicht → Bearbeiten → Zahlungsmethoden) und gilt für alle Portale.'
              : 'Bisherige Icons dieser Karte. Ändern: am Broker auswählen (Übersicht → Bearbeiten → Zahlungsmethoden) — dann gilt die Auswahl für alle Portale.'}
          </Typography>
        </Box>
      );
    }
    const kind = inferKind(key, card[key]);
    const known = KNOWN_BY_KEY.get(key);
    const label = labelFor(key);

    if (kind === 'stringList') {
      const arr = Array.isArray(card[key])
        ? (card[key] as unknown[]).map((v) => String(v ?? ''))
        : [];
      return (
        <StringListField
          label={label}
          hint={known?.hint}
          value={arr}
          onChange={(v) => setField(key, v)}
        />
      );
    }
    if (kind === 'objectList') {
      const rows = Array.isArray(card[key]) ? (card[key] as Record<string, unknown>[]) : [];
      return (
        <ObjectListField
          fieldKey={key}
          label={label}
          value={rows}
          onChange={(v) => setField(key, v)}
          onError={setUploadError}
          baseUrl={siteBaseUrl}
        />
      );
    }
    if (kind === 'image') {
      return (
        <ImageInput
          label={label}
          value={String(card[key] ?? '')}
          onChange={(url) => setField(key, url)}
          hint={known?.hint}
          onError={setUploadError}
          baseUrl={siteBaseUrl}
        />
      );
    }
    if (key === 'logo_scale') {
      return (
        <LogoScaleField
          value={card[key]}
          logo={String(card.logo ?? '')}
          siteKey={siteKey}
          baseUrl={siteBaseUrl}
          onChange={(v) => setField(key, v)}
        />
      );
    }
    if (kind === 'number') {
      // Free-text while editing; coerced to a real number at save (normalizeCardData).
      return (
        <TextField
          label={label}
          value={String(card[key] ?? '')}
          onChange={(e) => setField(key, e.target.value)}
          size="small"
          fullWidth
          inputProps={{ inputMode: 'decimal' }}
          helperText={known?.hint}
        />
      );
    }
    // string / text
    return (
      <TextField
        label={label}
        value={String(card[key] ?? '')}
        onChange={(e) => setField(key, e.target.value)}
        size="small"
        fullWidth
        multiline={kind === 'text'}
        minRows={kind === 'text' ? 2 : undefined}
        helperText={known?.hint}
      />
    );
  };

  return (
    <Box p={2} display="flex" flexDirection="column" gap={2} maxWidth={620}>
      <Alert severity="info" sx={{ borderRadius: 1 }}>
        Bearbeitet die <strong>Übersichtskarte</strong> dieses Profils (auf der /broker-Seite). Nach
        dem Speichern <strong>Deploy</strong> klicken, damit die Änderung live geht.
      </Alert>
      {siteKey === 'sw' && (
        <Alert severity="warning" sx={{ borderRadius: 1 }}>
          Hinweis Stock-World: die Lizenz-Anzeige ist im Portal-Template an den <em>Namen</em>{' '}
          gekoppelt – ein Lizenz-Feld hier bleibt auf SW wirkungslos.
        </Alert>
      )}
      {uploadError && (
        <Alert severity="error" onClose={() => setUploadError(null)} sx={{ borderRadius: 1 }}>
          {uploadError}
        </Alert>
      )}

      {orderedKeys.length === 0 && (
        <Typography color="text.secondary">
          Keine Kartendaten. Felder unten hinzufügen, um die Übersichtskarte aufzubauen.
        </Typography>
      )}

      {orderedKeys.map((key, idx) => (
        <React.Fragment key={key}>
          {idx > 0 && <Divider flexItem />}
          <Box display="flex" gap={1} alignItems="flex-start">
            <Box flex={1}>{renderField(key)}</Box>
            {key !== AM_BROKER_GEPFLEGT && (
              <Tooltip title="Feld entfernen">
                <IconButton size="small" onClick={() => removeField(key)} sx={{ mt: 0.5 }}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </React.Fragment>
      ))}

      <Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddOutlined />}
          disabled={missingFields.length === 0}
          onClick={(e) => setAddAnchor(e.currentTarget)}
        >
          Feld hinzufügen
        </Button>
        <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={() => setAddAnchor(null)}>
          {missingFields.map((f) => (
            <MenuItem key={f.key} onClick={() => addField(f)}>
              {f.label}
              <Chip label={f.key} size="small" variant="outlined" sx={{ ml: 1 }} />
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
};

export default CardEditor;
